import React, { useEffect } from "react";
import { Phone, Sparkles, Mail, UserPlus, Star, ArrowRight, Instagram } from "lucide-react";
import { applyOGTags } from "../lib/ogTags";

// Build a vCard string the user's phone will recognize as a contact card.
const VCARD = [
  "BEGIN:VCARD",
  "VERSION:3.0",
  "FN:The Pool Guru Treasure Coast",
  "ORG:The Pool Guru Treasure Coast",
  "TITLE:Pool Cleaning Experts",
  "TEL;TYPE=CELL,VOICE;PREF=1:+17722890405",
  "TEL;TYPE=CELL,VOICE:+17727107989",
  "EMAIL;TYPE=INTERNET,WORK:info@thepoolgurupsl.com",
  "URL:https://poolgurutc.com",
  "NOTE:Family-owned pool service across the Treasure Coast — Port St. Lucie\\, Fort Pierce\\, Stuart\\, Palm City\\, Jensen Beach.",
  "END:VCARD",
].join("\r\n");

function saveContact() {
  const blob = new Blob([VCARD], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "The-Pool-Guru.vcf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// HIDDEN customer-welcome page.
// - Not linked anywhere in the site navigation.
// - <meta name="robots" content="noindex,nofollow"> is set so Google won't index it.
// - Intended URL: /welcome (shared by SMS/email to new customers).
export default function WelcomePage() {
  useEffect(() => {
    document.title = "Welcome — The Pool Guru Treasure Coast";
    const id = "welcome-noindex";
    document.getElementById(id)?.remove();
    const m = document.createElement("meta");
    m.id = id;
    m.name = "robots";
    m.content = "noindex,nofollow";
    document.head.appendChild(m);

    // Override OG tags for SMS/iMessage previews on this page only.
    applyOGTags({
      title: "Please Save Our Contact — Thank You For Choosing Us",
      description:
        "The Pool Guru Treasure Coast — tap to save our contact info and view our latest reels. Call 772-289-0405.",
    });

    // Load Instagram embed script (once). If already loaded, just re-process.
    const SCRIPT_ID = "instagram-embed-script";
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.async = true;
      s.src = "https://www.instagram.com/embed.js";
      document.body.appendChild(s);
    } else if (window.instgrm && window.instgrm.Embeds) {
      window.instgrm.Embeds.process();
    }

    return () => document.getElementById(id)?.remove();
  }, []);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-brand-pink/10 via-white to-brand-pink/5 flex items-center justify-center py-12 px-4"
      data-testid="welcome-page"
    >
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl ring-1 ring-slate-100 overflow-hidden">
        {/* Header band with mascot */}
        {/* Header — clean white surface with the logo only */}
        <div className="px-6 pt-8 pb-2 text-center bg-white">
          <img
            src="/poolguru-logo.png"
            alt="The Pool Guru Treasure Coast"
            className="w-36 h-36 object-contain mx-auto mb-2"
          />
          <h1 className="text-2xl font-extrabold leading-snug tracking-tight text-brand-navy">
            Thank you for choosing The Pool Guru!
          </h1>
          <p className="text-sm mt-1 text-brand-pink font-semibold flex items-center justify-center gap-1.5">
            <Sparkles className="w-4 h-4" /> We Clean Your Pool
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-6 text-slate-700 leading-relaxed">
          <p className="mb-4">
            If you have any questions or need assistance, please feel free to reach out.
            <strong> Please save our numbers</strong> so you can easily contact us whenever needed.
          </p>

          <p className="mb-6">
            We are a <strong>family-owned and operated husband-and-wife team</strong> dedicated to
            providing reliable, personalized pool service.
          </p>

          {/* Save Contact button — drops a .vcf into the user's phone contacts */}
          <button
            type="button"
            onClick={saveContact}
            data-testid="welcome-save-contact"
            className="w-full bg-brand-pink hover:bg-brand-pink-hover text-white font-extrabold text-lg rounded-2xl px-5 py-4 mb-5 shadow-xl shadow-pink-500/40 ring-2 ring-white/60 transition-transform active:scale-95 flex items-center justify-center gap-3"
          >
            <UserPlus className="w-6 h-6" />
            Click Here to Save Contact
          </button>

          {/* Contact cards */}
          <div className="space-y-3 mb-6">
            <a
              href="tel:7727107989"
              data-testid="welcome-call-melissa"
              className="flex items-center justify-between bg-slate-50 hover:bg-brand-pink hover:text-white rounded-xl px-4 py-3 transition-colors border border-slate-200"
            >
              <div>
                <div className="text-xs uppercase tracking-wider opacity-75 font-bold">Melissa</div>
                <div className="text-lg font-extrabold">772-710-7989</div>
              </div>
              <div className="w-10 h-10 rounded-full bg-brand-pink/15 grid place-items-center group-hover:bg-white/20">
                <Phone className="w-5 h-5 text-brand-pink" />
              </div>
            </a>

            <a
              href="tel:7722890405"
              data-testid="welcome-call-darren"
              className="flex items-center justify-between bg-slate-50 hover:bg-brand-pink hover:text-white rounded-xl px-4 py-3 transition-colors border border-slate-200"
            >
              <div>
                <div className="text-xs uppercase tracking-wider opacity-75 font-bold">Darren</div>
                <div className="text-lg font-extrabold">772-289-0405</div>
              </div>
              <div className="w-10 h-10 rounded-full bg-brand-pink/15 grid place-items-center">
                <Phone className="w-5 h-5 text-brand-pink" />
              </div>
            </a>

            <a
              href="mailto:info@thepoolgurupsl.com"
              data-testid="welcome-email"
              className="flex items-center justify-between bg-slate-50 hover:bg-brand-pink hover:text-white rounded-xl px-4 py-3 transition-colors border border-slate-200"
            >
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider opacity-75 font-bold">Email</div>
                <div className="text-base font-extrabold truncate">info@thepoolgurupsl.com</div>
              </div>
              <div className="w-10 h-10 rounded-full bg-brand-pink/15 grid place-items-center flex-shrink-0 ml-2">
                <Mail className="w-5 h-5 text-brand-pink" />
              </div>
            </a>
          </div>

          {/* Look for the Pink Truck */}
          <div className="mt-6 mb-2">
            <div className="text-center mb-3">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-pink font-bold mb-1">Spot Us Around Town</div>
              <div className="text-xl font-extrabold text-brand-navy">Look for the Flamingo Pink Truck</div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl ring-2 ring-brand-pink/20">
              <img
                src="https://customer-assets.emergentagent.com/job_e7f57bd1-2795-438b-9b0c-4000904afe35/artifacts/s0xiip7s_IMG_9067.jpeg"
                alt="The Pool Guru pink service truck on the Treasure Coast"
                className="w-full h-auto block"
                data-testid="welcome-truck-image"
              />
            </div>
          </div>

          <p className="text-center text-slate-600 mt-6">
            We appreciate your business and look forward to keeping your pool
            <strong className="text-brand-navy"> clean, clear, and ready to enjoy!</strong>
          </p>
          <div className="text-center text-2xl mt-3" aria-hidden="true">🏊‍♂️✨</div>

          {/* Read reviews */}
          <a
            href="https://www.poolgurutc.com/reviews"
            target="_blank"
            rel="noreferrer"
            data-testid="welcome-read-reviews"
            className="mt-6 flex items-center justify-between bg-white hover:bg-brand-pink hover:text-white rounded-xl px-4 py-3 border-2 border-brand-pink/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-pink/15 grid place-items-center">
                <Star className="w-5 h-5 text-brand-pink fill-brand-pink" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider opacity-75 font-bold">Customer Reviews</div>
                <div className="text-base font-extrabold">Read what our customers say</div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 flex-shrink-0 ml-2" />
          </a>

          {/* Instagram — three latest posts/reels */}
          <div className="mt-6">
            <div className="text-center mb-3">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-pink font-bold mb-1">Follow Us</div>
              <div className="text-xl font-extrabold text-brand-navy">Latest on Instagram</div>
            </div>
            <div className="grid grid-cols-1 gap-4" data-testid="welcome-instagram-grid">
              {[
                "https://www.instagram.com/reel/DZXMiiTRDgh/",
                "https://www.instagram.com/reel/DPWadyikfVj/",
                "https://www.instagram.com/p/DPRmD1wkXwP/",
              ].map((url) => (
                <blockquote
                  key={url}
                  className="instagram-media"
                  data-instgrm-permalink={url}
                  data-instgrm-version="14"
                  style={{
                    background: "#FFF",
                    border: 0,
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    margin: 0,
                    maxWidth: "540px",
                    minWidth: "200px",
                    padding: 0,
                    width: "100%",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Instagram — link to profile */}
          <a
            href="https://www.instagram.com/poolgurutc/"
            target="_blank"
            rel="noreferrer"
            data-testid="welcome-instagram"
            className="mt-4 flex items-center justify-between rounded-xl px-4 py-3 text-white transition-transform active:scale-95"
            style={{
              background:
                "linear-gradient(135deg, #f9ce34 0%, #ee2a7b 50%, #6228d7 100%)",
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 grid place-items-center">
                <Instagram className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider opacity-90 font-bold">Follow Us</div>
                <div className="text-base font-extrabold">@poolgurutc on Instagram</div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 flex-shrink-0 ml-2" />
          </a>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 text-center border-t border-slate-100">
          <a
            href="https://www.poolgurutc.com"
            className="text-sm text-brand-navy/70 hover:text-brand-pink font-medium"
          >
            poolgurutc.com
          </a>
        </div>
      </div>
    </div>
  );
}
